import { MotionProps, Easing } from "motion/react";
import { useIsNavigatingBack } from "../contexts";

export type TUseAnimateFunc = (config?: TAnimationConfig, conditions?: TAnimationConditions) => MotionProps | undefined;

export type TAnimationConfigAndConditions = {
  config: TAnimationConfig;
  conditions: TAnimationConditions;
};

export type TAnimationConfig = {
  delay?: number;
  whileInView?: boolean;
  duration?: number;
  easing?: Easing;
  displayAmountTreshold?: number;
};

export type TAnimationConditions = {
  alwaysAnimate?: boolean;
  blockIndex?: number;
};

const defaultConfig: TAnimationConfig = {
  delay: 0,
  whileInView: false,
  duration: undefined,
  easing: undefined,
};

const defaultConditions: TAnimationConditions = {
  alwaysAnimate: false,
  blockIndex: undefined,
};

const useBuildAnimation = (
  animationConfig: TAnimationConfigAndConditions,
  animateFrom: any,
  animateTo: any,
): MotionProps | undefined => {
  const { delay, whileInView, duration, easing, displayAmountTreshold } = animationConfig.config;
  const { alwaysAnimate, blockIndex } = animationConfig.conditions;

  const isNavigatingBack = useIsNavigatingBack();
  if (isNavigatingBack && !alwaysAnimate) {
    return undefined;
  }

  const whileInViewAndNotFirst = whileInView && blockIndex !== 0;

  return {
    initial: animateFrom,
    transition: {
      delay: delay,
      duration: duration,
      ease: easing,
    },
    whileInView: whileInViewAndNotFirst ? animateTo : undefined,
    animate: whileInViewAndNotFirst ? undefined : animateTo,
    viewport: whileInViewAndNotFirst ? { once: true, amount: displayAmountTreshold ?? 0.25 } : undefined,
  };
};

export const useAnimateFadeInToTop: TUseAnimateFunc = (config = defaultConfig, conditions = defaultConditions) => {
  const animateFrom = {
    opacity: 0,
    y: 20,
  };

  const animateTo = {
    opacity: 1,
    y: 0,
  };

  return useBuildAnimation({ config, conditions }, animateFrom, animateTo);
};

export const useAnimateSlideToTop: TUseAnimateFunc = (config = defaultConfig, conditions = defaultConditions) => {
  const animateFrom = {
    y: 20,
  };

  const animateTo = {
    y: 0,
  };

  return useBuildAnimation({ config, conditions }, animateFrom, animateTo);
};

export const useAnimateFadeIn: TUseAnimateFunc = (config = defaultConfig, conditions = defaultConditions) => {
  const animateFrom = {
    opacity: 0,
  };

  const animateTo = {
    opacity: 1,
  };

  return useBuildAnimation({ config, conditions }, animateFrom, animateTo);
};

export const getSlideRightAnimateTo = () => {
  const animateTo = {
    x: 8,
  };

  return animateTo;
};
